/**
 * A V4 alarm carries an `origin` snapshot of whatever raised it — the SDK's
 * `AlarmOriginSnapshot` union of device / user / api_token / system. The alarm UI is
 * device-keyed, so this flattens any origin down to the id + name that UI needs.
 */

import { prettifyV4Type } from './DeviceType';

export const V4_ALARM_ORIGIN = {
    DEVICE: 'device',
    USER: 'user',
    API_TOKEN: 'api_token',
    SYSTEM: 'system',
};

// Origin kind -> i18n key. `system` reads as its only subtype today (`scenario`).
// NOTE: `apiToken` still needs seeding in the V4 backend; the other three exist.
const LABEL_KEY = {
    [V4_ALARM_ORIGIN.DEVICE]: 'device',
    [V4_ALARM_ORIGIN.USER]: 'user',
    [V4_ALARM_ORIGIN.API_TOKEN]: 'apiToken',
    [V4_ALARM_ORIGIN.SYSTEM]: 'scenario',
};

/** V4 users have no display name of their own — same join as the users page. */
const userName = (user) =>
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '';

/**
 * Normalises an alarm's origin to `{type, id, name, labelKey, device}`. `device` is null
 * for every non-device origin, so callers can tell a real device from a stand-in.
 * Falls back to the pre-origin flat `alarm.device` for older payloads.
 */
export function v4AlarmOrigin(v4Alarm) {
    const origin = v4Alarm?.origin;
    const type = origin?.type;

    if (type === V4_ALARM_ORIGIN.USER) {
        const user = origin.user || {};
        return {
            type,
            id: user.id || '',
            name: userName(user),
            labelKey: LABEL_KEY[type],
            device: null,
        };
    }

    if (type === V4_ALARM_ORIGIN.API_TOKEN) {
        const token = origin.api_token || {};
        return {
            type,
            id: token.id || '',
            name: token.name || '',
            labelKey: LABEL_KEY[type],
            device: null,
        };
    }

    if (type === V4_ALARM_ORIGIN.SYSTEM) {
        const system = origin.system || {};
        return {
            type,
            // The subsystem's own record — today, the scenario that fired.
            id: system.source_id || '',
            // `name` is optional, `trigger` is not; prettified, it arrives as `geofence_exit`.
            name: system.name || prettifyV4Type(system.trigger),
            labelKey: LABEL_KEY[type],
            device: null,
        };
    }

    // An origin kind added after this code was written: report it with no device, or
    // callers wait on one that can never arrive. Label falls back to a key we know exists.
    if (type && type !== V4_ALARM_ORIGIN.DEVICE) {
        return {
            type,
            id: '',
            name: '',
            labelKey: LABEL_KEY[type] || LABEL_KEY[V4_ALARM_ORIGIN.DEVICE],
            device: null,
        };
    }

    // A device origin, or the legacy flat field. Stays null when the snapshot is missing,
    // so a device origin carrying no device is never mistaken for a resolvable one.
    const device = (type === V4_ALARM_ORIGIN.DEVICE ? origin.device : v4Alarm?.device) || null;
    return {
        type: V4_ALARM_ORIGIN.DEVICE,
        id: device?.id || 0,
        name: device?.name || '',
        labelKey: LABEL_KEY[V4_ALARM_ORIGIN.DEVICE],
        device,
    };
}

/** The raw V4 device that raised the alarm, or null when something else did. */
export function v4AlarmOriginDevice(v4Alarm) {
    return v4AlarmOrigin(v4Alarm).device;
}
